/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 *  ******************************************************************************
 */

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { concat, Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map } from 'rxjs/operators';
import { sendSmsMessageFail, sendTddMessageFail, translateAndSendMessage } from '../../call/+state/call.actions';
import { selectCallsMap } from '../../call/+state/call.selectors';
import {
    fetchTranslationLanguagesFail,
    fetchTranslationLanguagesSuccess,
    patchDialogLanguage,
    patchDialogLanguageFail,
    patchDialogLanguageSuccess,
    fetchDialogState,
    fetchDialogStateFail,
    fetchDialogStateSuccess,
    MessageErrorType,
    messageErrorTranslationFailed,
    messageErrorMarkTranslationError,
    messageErrorRemoveTranslationError,
    messageErrorRetry,
    messageErrorRemove,
    pendingMessageAdded,
    pendingMessageTranslated,
    pendingMessageRemove
} from './message.actions';
import { TextTranslationService } from '../services/text-translation.service';
import { EnvironmentService } from '../../core/services/environment.service';
import { fetchTranslationSettingsSuccess } from '../../configuration/+state/configuration.actions';
import { displayToastNotification } from '../../notification/+state/notification.actions';
import { ToastType } from '@msi/cobalt';
import { anonymizeForLog, createPlaceholderMessage, generateMessageId } from "../util/message.util";
import { buildSendMessageAction, selectTranslationStrategy } from "../util/translation.strategy.util";

@Injectable()
export class TranslationEffects {

    constructor(
        private actions$: Actions,
        private store: Store,
        private translationService: TextTranslationService,
        private envService: EnvironmentService
    ) {}

    // Fetches available translation languages from the translation service
    fetchTranslationLanguages$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchTranslationSettingsSuccess),
            filter(({ translationEnabled }) => {
                const envFlag = !!(this.envService.environment?.featureFlags?.translationsEnabled);
                return envFlag && translationEnabled;
            }),
            concatMap(() =>
                this.translationService.getDialogLanguages().pipe(
                    map((languages) => fetchTranslationLanguagesSuccess({ languages })),
                    catchError((err) => {
                        console.error('fetchTranslationLanguages$ — error', err);
                        return of(fetchTranslationLanguagesFail({ payload: String(err) }));
                    })
                )
            )
        )
    );

    // Fetches the dialog state for a given callId from the translation service
    fetchDialogState$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchDialogState),
            concatMap(({ callId }) =>
                this.translationService.getDialogState(callId).pipe(
                    map((dialogState) => fetchDialogStateSuccess({ callId, dialogState })),
                    catchError((err) => {
                        console.error(`fetchDialogState$ — error callId=${callId}`, err);
                        return of(fetchDialogStateFail({ callId, payload: String(err) }));
                    })
                )
            )
        )
    );

    // Persists an override/detected dialog language for a given callId
    patchDialogLanguage$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(patchDialogLanguage),
            concatMap(({ callId, languageCode, languageChangeSource }) =>
                this.translationService.patchDialogLanguage(callId, languageCode).pipe(
                    map(() => patchDialogLanguageSuccess({ callId, languageCode, languageChangeSource })),
                    catchError((err) => {
                        console.error(`patchDialogLanguage$ — ERROR callId=${callId} languageCode=${languageCode}`, err);
                        return of(patchDialogLanguageFail({ callId, languageCode, error: String(err) }));
                    })
                )
            )
        )
    );

    translateAndSendMessage$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(translateAndSendMessage),
            concatMap((action) => {
                const messageId = action.existingMessageId ?? generateMessageId();
                return this.translateAndSend(action, messageId);
            })
        )
    );

    private translateAndSend(action: ReturnType<typeof translateAndSendMessage>, messageId: string) {
        const { callId, clusterName, message, messageType, predefined, language, username, patchLanguageCode } = action;
        const participant = { type: 'CALL_TAKER' as const };
        const translate$ = patchLanguageCode
            ? this.translationService.patchDialogLanguage(callId, patchLanguageCode).pipe(
                concatMap(() => this.translationService.translateMessage(callId, messageId, message, participant))
            )
            : this.translationService.translateMessage(callId, messageId, message, participant);
        const placeholder = createPlaceholderMessage(action, messageId);

        return concat(
            // 1. Emit placeholder immediately — visible as spinner in message list
            of(pendingMessageAdded({ message: placeholder, messageType })),
            // 2. (optional PATCH to commit a pending manual language selection, then) REST translation call
            translate$.pipe(
                concatMap(({ translatedText, targetLanguage: tsTargetLang, sourceLanguage: tsSourceLanguage }) => {
                    const translationOutcome = selectTranslationStrategy(message, messageId, translatedText, tsSourceLanguage, tsTargetLang);

                    console.debug(
                        'translateAndSendMessage$ [OUT] — sending' +
                        ` callId=${callId}` +
                        ` translationApplied=${translationOutcome.translationApplied}` +
                        ` sentOriginal=${translationOutcome.sentOriginal}` +
                        ` msgToSend="${anonymizeForLog(translationOutcome.msgToSend)}"` +
                        ` coreSourceText="${anonymizeForLog(translationOutcome.translationFields.sourceText)}"` +
                        ` coreTargetText="${anonymizeForLog(translationOutcome.translationFields.targetText)}"` +
                        ` coreMessageLanguage="${translationOutcome.translationFields.messageLanguage}"` +
                        ` messageOverride=${Boolean(translationOutcome.placeholderMessageOverride)}`
                    );

                    const updateAction = pendingMessageTranslated({
                        messageId,
                        message: translationOutcome.placeholderMessageOverride,
                        ...translationOutcome.translationFields
                    });
                    const sendAction = buildSendMessageAction(
                        messageType, translationOutcome.msgToSend, callId, clusterName, predefined, language, translationOutcome.translationFields
                    );

                    return concat(of(updateAction), of(sendAction));
                }),
                catchError((err) => {
                    console.error(`translateAndSendMessage$ [OUT] — translateMessage error callId=${callId}`, err);
                    console.warn(
                        `translateAndSendMessage$ [OUT] — TS FAILED, awaiting user action callId=${callId} messageId=${messageId} text="${anonymizeForLog(message)}"`
                    );
                    return of(
                        messageErrorTranslationFailed({
                            messageType,
                            message,
                            callId,
                            clusterName,
                            predefined: Boolean(predefined),
                            language: language ?? '',
                            messageId,
                            userName: username ?? ''
                        })
                    );
                })
            )
        );
    }

    translationFailed$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(messageErrorTranslationFailed),
            map(({ messageId, messageType, message, callId, clusterName, predefined, language, userName }) => {
                return messageErrorMarkTranslationError({
                    messageId, messageType, message, callId, clusterName, predefined, language, userName
                });
            })
        )
    );

    translationErrorSendWithout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(messageErrorRemoveTranslationError),
            map(({ messageType, message, callId, clusterName, predefined, language, messageId }) => {
                return buildSendMessageAction(messageType, message, callId, clusterName, predefined, language, { clientMessageId: messageId });
            })
        )
    );

    messageErrorRetry$ = createEffect(() =>
        this.actions$.pipe(
            ofType(messageErrorRetry),
            concatLatestFrom(() => this.store.select(selectCallsMap)),
            map(([action, callsMap]) => {
                const { messageId, errorType, messageType, message, callId, predefined, language, username,
                    sourceText, sourceLanguage, targetText, targetLanguage, messageLanguage } = action;
                const clusterName = callsMap[callId]?.clusterName ?? action.clusterName;

                switch (errorType) {
                    case MessageErrorType.TRANSLATION:
                        return translateAndSendMessage({
                            callId, clusterName, message, messageType, predefined,
                            language, username, existingMessageId: messageId,
                            patchLanguageCode: language
                        });
                    case MessageErrorType.SEND:
                    case MessageErrorType.DELIVERY:
                        return buildSendMessageAction(messageType, message, callId, clusterName, predefined, language, {
                            clientMessageId: messageId, sourceText, sourceLanguage, targetText, targetLanguage, messageLanguage
                        });
                    default:
                        return { type: '[Message] Unknown Error Type' };
                }
            })
        )
    );

    messageErrorRemove$ = createEffect(() =>
        this.actions$.pipe(
            ofType(messageErrorRemove),
            map(({ messageId }) => pendingMessageRemove({ messageId }))
        )
    );

    sendMessageFail$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(sendSmsMessageFail, sendTddMessageFail),
            map(() => displayToastNotification({ message: 'Failed to send message. Please try again.', level: ToastType.error }))
        )
    );
}
